import React from 'react';
import { ColumnDef, OnChangeFn, SortingState } from '@tanstack/react-table';
import MobileCard from './MobileCards';
import DataTable from '../common/DataTable';

// ─── useMediaQuery hook (add to @/hooks/useMediaQuery.ts if not present) ─────
// If you already have one, remove this and import from there.
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );

  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    setMatches(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface ResponsiveDataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];

  // ── Mobile-only ────────────────────────────────────────────────────────────
  /**
   * Column keys always shown on the card face.
   *
   * Member table recommendation:
   *   ['userName', 'balance', 'exposure', 'totalpl', 'status']
   *
   * Status is auto-detected and moved to the top-right badge.
   * Everything else goes to the expandable drawer.
   */
  pinnedKeys?: string[];

  /**
   * Column keys rendered as the action row on the card face.
   * Default: ['actions']
   */
  actionKeys?: string[];

  // ── Shared (passed straight through to DataTable / MobileCard) ────────────
  onSelectionChange?: (selectedRows: TData[]) => void;
  enableSelection?: boolean;
  enableAccordion?: boolean;
  pageSize?: number;
  variant?: 'default' | 'primary' | 'secondary';
  isLoading?: boolean;
  renderSubRow?: (rowData: TData) => React.ReactNode;
  accordionPosition?: number;
  headerClassName?: string;
  rowvariant?: 'default' | 'green';
  total?: boolean;
  totals?: Record<string, unknown>;
  rows?: number;
  rowClassNames?: string;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualSorting?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ResponsiveDataTable<TData>({
  data,
  columns,

  // mobile
  pinnedKeys,
  actionKeys = ['actions'],

  // shared
  onSelectionChange,
  enableSelection = false,
  enableAccordion = false,
  pageSize = 10,
  variant = 'default',
  rowvariant = 'default',
  total,
  totals,
  isLoading,
  renderSubRow,
  accordionPosition,
  headerClassName,
  rows = 10,
  rowClassNames,
  sorting,
  onSortingChange,
  manualSorting,
}: ResponsiveDataTableProps<TData>) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  if (isMobile) {
    return (
      <MobileCard
        data={data}
        columns={columns}
        pinnedKeys={pinnedKeys}
        actionKeys={actionKeys}
        isLoading={isLoading}
        rows={rows}
        renderSubRow={renderSubRow}
        enableAccordion={enableAccordion}
        rowClassNames={rowClassNames}
      />
    );
  }

  return (
    <DataTable
      data={data}
      columns={columns}
      onSelectionChange={onSelectionChange}
      enableSelection={enableSelection}
      enableAccordion={enableAccordion}
      pageSize={pageSize}
      variant={variant}
      rowvariant={rowvariant}
      total={total}
      totals={totals}
      isLoading={isLoading}
      renderSubRow={renderSubRow}
      accordionPosition={accordionPosition}
      headerClassName={headerClassName}
      rows={rows}
      rowClassNames={rowClassNames}
      sorting={sorting}
      onSortingChange={onSortingChange}
      manualSorting={manualSorting}
    />
  );
}

export default ResponsiveDataTable;
